<template>
  <div class="order-day-row">
    <!-- Day info -->
    <div class="day-container">
      <div class="week-day">
        {{ weekDay }}
      </div>
      <div class="day-number-container">
        <span
          class="day-number"
          :class="{ today: isToday }"
        >
          {{ dayNumber }}
        </span>
      </div>
    </div>

    <!-- Order content -->
    <div
      class="order-content"
      :class="{
        'order-content--disabled': !offerAvailable,
        'order-content--clickable': offerAvailable
      }"
      @click="handleDayClick"
    >
      <!-- No offer available info -->
      <div
        v-if="orderBlockReason && showNoOfferInfo"
        class="order-item order-item--no-offer"
      >
        <div>
          <span class="no-offer-text">{{ orderBlockReason.otherOrderBlock }}</span>
        </div>
      </div>
      <div
        v-else-if="showNoOfferInfo"
        class="order-item order-item--no-offer"
      >
        <div>
          <span class="no-offer-text">Brak oferty</span>
        </div>
      </div>

      <!-- Existing orders -->
      <div
        v-if="dayOrders.length > 0"
        class="order-item order-item--existing"
      >
        <div
          v-if="isUnPaid || isDelivered"
          class="d-flex align-items-center"
          style="margin-bottom: -6px; padding-top: 5px"
        >
          <div
            v-if="isUnPaid"
            class="text-warning px-2"
            style="font-size: 12px"
          >
            <i class="fas fa-triangle-exclamation" />
            Nieopłacone
          </div>
          <div
            v-if="isDelivered"
            class="text-success px-2"
            style="font-size: 12px"
          >
            <i class="fas fa-hand-holding-box" />
            Odebrane
          </div>
        </div>
        <div class="order-item-content">
          <div class="order-item-main">
            <div class="order-item-header">
              <span
                class="order-item-names"
              >{{ orderedNames }}</span>
            </div>
            <div class="order-item-price">
              <Money
                :value="orderedPrice"
                :currency="dayOrders[0].currency"
              />
            </div>
            <div
              v-if="orderedDescriptions"
              class="order-item-description"
            >
              {{ orderedDescriptions }}
            </div>
          </div>
          <div class="order-item-actions">
            <i class="fas fa-pen" />
            <span class="action-text">{{ $t('shop.change') }}</span>
          </div>
        </div>
      </div>

      <!-- Returned basket orders -->
      <div
        v-if="returnBasketOrders.length > 0"
        class="order-item order-item--basket"
      >
        <div class="order-item-content">
          <div class="order-item-main">
            <div class="order-item-header">
              <i class="fas fa-cart-plus basket-icon" />
              <span
                class="order-item-names"
              >{{ returnOfferNames }}</span>
            </div>
            <div class="order-item-price">
              <Money
                :value="returnDayPrice"
                :currency="returnBasketOrders[0].currency"
              />
            </div>
          </div>
          <div class="order-item-actions">
            <i class="fas fa-pen" />
            <span class="action-text">{{ $t('shop.change') }}</span>
          </div>
        </div>
      </div>

      <!-- Basket orders -->
      <div
        v-if="basketOrders.length > 0"
        class="order-item order-item--basket"
      >
        <div class="order-item-content">
          <div class="order-item-main">
            <div class="order-item-header">
              <i class="fas fa-cart-plus basket-icon" />
              <span
                class="order-item-names"
              >{{ offerNames }}</span>
            </div>
            <div class="order-item-price">
              <Money
                :value="dayPrice"
                :currency="basketOrders[0].currency"
              />
            </div>
            <div
              v-if="offerDescriptions"
              class="order-item-description"
            >
              {{ offerDescriptions }}
            </div>
          </div>
          <div class="order-item-actions">
            <i class="fas fa-pen" />
            <span class="action-text">{{ $t('shop.change') }}</span>
          </div>
        </div>
      </div>

      <!-- Order button -->
      <div
        v-if="showOrderButton"
        class="order-item order-item--button"
      >
        <div class="order-button">
          <div class="order-button-content">
            <i class="fas fa-plus" />
            <span>Zamów</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Money from '@/components/Money';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
  name: 'OrderDayRow',
  props: {
    day: {
      type: Object, // moment object
      required: true,
    },
    orders: {
      type: Object,
      required: true,
    },
    payer: {
      type: Object,
      required: true,
    },
    catalog: {
      type: Object,
      required: true,
    },
    offerCalendar: {
      type: Object,
      required: true,
    },
  },
  components: {
    Money,
  },
  computed: {
    ...mapGetters(['catalogBasket']),

    weekDay() {
      return this.day.format('ddd');
    },

    dayNumber() {
      return this.day.format('D');
    },

    isToday() {
      return this.day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
    },
    isUnPaid() {
      return this.dayOrders.some((x) => !x.isPaid);
    },
    isDelivered() {
      return this.dayOrders.some((x) => x.delivered > 0);
    },

    dayKey() {
      return this.day.format('YYYY-MM-DD');
    },

    basketOrders() {
      return this.catalogBasket
        .filter((b) => b.day === this.dayKey)
        .filter((x) => x.payerId === this.payer.payerId)
        .filter((x) => !x.isReturned)
        .map((x) => ({
          ...x,
          price: x.isReturned ? -x.price : x.price,
        }));
    },
    returnBasketOrders() {
      return this.catalogBasket
        .filter((b) => b.day === this.dayKey)
        .filter((x) => x.payerId === this.payer.payerId)
        .filter((x) => x.isReturned)
        .map((x) => ({
          ...x,
          price: x.isReturned ? -x.price : x.price,
        }));
    },

    dayPrice() {
      return this.basketOrders.reduce((acc, curr) => acc + curr.price * curr.count, 0);
    },

    returnDayPrice() {
      return this.returnBasketOrders.reduce((acc, curr) => acc + curr.price * curr.count, 0);
    },

    offerNames() {
      return this.basketOrders.map((b) => (b.count === 1 ? b.offerName : `${b.count}x ${b.offerName}`)).join(', ');
    },

    returnOfferNames() {
      return this.returnBasketOrders
        .map((b) => (b.count === 1 ? b.offerName : `${b.count}x ${b.offerName}`)).join(', ');
    },

    offerDescriptions() {
      return this.basketOrders
        .map((b) => this.catalog.listings.find((l) => l.id === b.offerId)?.description)
        .filter((x) => x)
        .join(', ');
    },

    dayOrders() {
      return this.orders.orders
        .filter((o) => o.day === this.dayKey)
        .map((o) => ({
          ...o,
          listing: this.orders.productSets.find((p) => p.id === o.productSetId),
        }));
    },

    orderedPrice() {
      return this.dayOrders.reduce(
        (acc, curr) => acc + curr.unitPrice * curr.count,
        0,
      );
    },

    orderedNames() {
      return Object.values(this.dayOrders
        .reduce((acc, curr) => {
          if (acc[curr.listing.id]) {
            acc[curr.listing.id].count += curr.count;
          } else {
            acc[curr.listing.id] = curr;
          }
          return acc;
        }, {}))
        .map((b) => (b.count === 1 ? (b.listing?.name || '?') : `${b.count}x ${b.listing?.name || '?'}`)).join(', ');
    },

    orderedDescriptions() {
      return this.dayOrders
        .map((b) => b.listing?.description)
        .filter((x) => x)
        .join(', ');
    },

    offerAvailable() {
      return this.offerCalendar.calendar
        .filter((x) => x.canOrder)
        .some((x) => x.day === this.dayKey) || this.dayOrders.length > 0;
    },

    orderBlockReason() {
      return this.offerCalendar.calendar
        .filter((x) => x.day === this.dayKey)
        .find((x) => x.otherOrderBlock);
    },

    showDayModal() {
      return this.dayOrders.length === 0 && this.basketOrders.length === 0 && this.offerAvailable;
    },

    showNoOfferInfo() {
      return this.dayOrders.length === 0 && this.basketOrders.length === 0 && !this.offerAvailable;
    },

    showOrderButton() {
      return this.showDayModal;
    },
  },
  methods: {
    handleDayClick() {
      if (this.offerAvailable) {
        this.$emit('day-click', this.day);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.order-day-row {
  display: flex;
  margin-bottom: 10px;
}

.day-container {
  width: 50px;
  min-width: 50px;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.week-day {
  font-size: 10px;
  color: #bcbcbc;
  text-transform: uppercase;
  font-weight: 500;
  text-align: center;
}

.day-number-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-number {
  font-size: 14px;
  color: #868585;
  text-align: center;

  &.today {
    background-color: #007bff;
    color: white;
    width: 30px;
    padding: 3px;
    border-radius: 30px;
  }
}

.order-content {
  background-color: white;
  flex-grow: 1;
  border-radius: 5px;
  border: 1px solid #e0e0e0;

  &--disabled {
    opacity: 0.5;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}

.order-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  &--no-offer {
    padding: 10px;
  }

  &--existing,
  &--basket {
    .order-item-content {
      display: flex;
      justify-content: space-between;
      flex-shrink: 1;
      padding: 10px;
      width: 100%;
    }
  }

  &--basket {
    border-style: dashed;
    border-width: 1px;
    border-color: #007bff;
  }

  &--button {
    padding: 10px;
  }
}

.order-item-main {
  flex-shrink: 1;
}

.order-item-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  flex: 1;
}

.order-item-names {
  font-weight: 500;
  font-size: 13px;
  flex-wrap: wrap;
  width: 220px;
}

.basket-icon {
  font-size: 12px;
  color: #007bff;
  margin-right: 5px;
  position: relative;
  top: 4px;
}

.order-item-description {
  font-size: 12px;
  color: #9e9e9e;
  margin-top: 5px;
}

.order-item-actions {
  display: flex;
  align-items: center;
  width: 50px;

  i {
    color: #9e9e9e;
    font-size: 12px;
  }

  .action-text {
    font-size: 12px;
    color: #9e9e9e;
    margin-left: 5px;
  }
}

.order-button {
  padding: 5px 20px;
  border-radius: 15px;
  height: 30px;
  width: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #007bff;
}

.order-button-content {
  display: flex;
  align-items: center;
  color: white;

  i {
    margin-right: 5px;
    font-size: 12px;
  }
}

.no-offer-text {
  color: #9e9e9e;
  font-size: 10px;
}
</style>
